/**
 * Authentication types and interfaces
 */
import { UserRole } from '../utils/enums.js';
// Permission constants
export const PERMISSIONS = {
    // User management
    USERS_VIEW: 'users:view',
    USERS_CREATE: 'users:create',
    USERS_UPDATE: 'users:update',
    USERS_DELETE: 'users:delete',
    USERS_MANAGE: 'users:manage',
    // Customer management
    CUSTOMERS_VIEW: 'customers:view',
    CUSTOMERS_CREATE: 'customers:create',
    CUSTOMERS_UPDATE: 'customers:update',
    CUSTOMERS_DELETE: 'customers:delete',
    CUSTOMERS_MANAGE: 'customers:manage',
    // Job management
    JOBS_VIEW: 'jobs:view',
    JOBS_CREATE: 'jobs:create',
    JOBS_UPDATE: 'jobs:update',
    JOBS_DELETE: 'jobs:delete',
    JOBS_ASSIGN: 'jobs:assign',
    JOBS_MANAGE: 'jobs:manage',
    // Estimate management
    ESTIMATES_VIEW: 'estimates:view',
    ESTIMATES_CREATE: 'estimates:create',
    ESTIMATES_UPDATE: 'estimates:update',
    ESTIMATES_DELETE: 'estimates:delete',
    ESTIMATES_SEND: 'estimates:send',
    ESTIMATES_MANAGE: 'estimates:manage',
    // Invoice management
    INVOICES_VIEW: 'invoices:view',
    INVOICES_CREATE: 'invoices:create',
    INVOICES_UPDATE: 'invoices:update',
    INVOICES_DELETE: 'invoices:delete',
    INVOICES_SEND: 'invoices:send',
    INVOICES_MANAGE: 'invoices:manage',
    // Business settings
    BUSINESS_VIEW: 'business:view',
    BUSINESS_UPDATE: 'business:update',
    BUSINESS_DELETE: 'business:delete',
    BUSINESS_MANAGE: 'business:manage',
    BUSINESS_SETTINGS: 'business:settings',
    BUSINESS_INTEGRATIONS: 'business:integrations',
    // Reports and analytics
    REPORTS_VIEW: 'reports:view',
    REPORTS_EXPORT: 'reports:export',
    REPORTS_MANAGE: 'reports:manage',
    // Time tracking
    TIME_CLOCK: 'time:clock',
    TIME_MANAGE: 'time:manage',
    TIME_VIEW: 'time:view',
    TIME_APPROVE: 'time:approve',
    // Scheduling and route planning
    SCHEDULING_VIEW: 'scheduling:view',
    SCHEDULING_MANAGE: 'scheduling:manage',
    SCHEDULING_CREATE: 'scheduling:create',
    SCHEDULING_UPDATE: 'scheduling:update',
    SCHEDULING_DELETE: 'scheduling:delete',
    ROUTE_PLANNING_VIEW: 'route_planning:view',
    ROUTE_PLANNING_MANAGE: 'route_planning:manage',
    ROUTE_PLANNING_CREATE: 'route_planning:create',
    ROUTE_PLANNING_UPDATE: 'route_planning:update',
    ROUTE_PLANNING_DELETE: 'route_planning:delete',
    // Price book
    PRICEBOOK_VIEW: 'pricebook:view',
    PRICEBOOK_CREATE: 'pricebook:create',
    PRICEBOOK_UPDATE: 'pricebook:update',
    PRICEBOOK_DELETE: 'pricebook:delete',
    PRICEBOOK_MANAGE: 'pricebook:manage',
    // Inventory management
    INVENTORY_READ: 'inventory:read',
    INVENTORY_WRITE: 'inventory:write',
    INVENTORY_RECEIVE: 'inventory:receive',
    INVENTORY_APPROVE: 'inventory:approve',
    INVENTORY_MANAGE: 'inventory:manage',
    // Parts / Supplier Network
    SUPPLIER_NETWORK_READ: 'supplier_network:read',
    SUPPLIER_NETWORK_WRITE: 'supplier_network:write',
    SUPPLIER_NETWORK_MANAGE: 'supplier_network:manage',
    // Intake flows
    INTAKE_FLOWS_READ: 'intake_flows:read',
    INTAKE_FLOWS_CREATE: 'intake_flows:create',
    INTAKE_FLOWS_UPDATE: 'intake_flows:update',
    INTAKE_FLOWS_DELETE: 'intake_flows:delete',
    INTAKE_FLOWS_MANAGE: 'intake_flows:manage',
    INTAKE_FLOWS_EXECUTE: 'intake_flows:execute',
    // Fleet/Vehicle management
    FLEET_READ: 'fleet:read',
    FLEET_WRITE: 'fleet:write',
    FLEET_ASSIGN: 'fleet:assign',
    FLEET_MANAGE: 'fleet:manage',
    // Expense tracker
    EXPENSES_VIEW: 'expenses:view',
    EXPENSES_CREATE: 'expenses:create',
    EXPENSES_UPDATE: 'expenses:update',
    EXPENSES_APPROVE: 'expenses:approve',
    EXPENSES_MANAGE: 'expenses:manage',
    // AI access
    AI_ACCESS: 'ai:access',
    // Payment management
    PAYMENTS_VIEW: 'payments:view',
    PAYMENTS_CREATE: 'payments:create',
    PAYMENTS_UPDATE: 'payments:update',
    PAYMENTS_DELETE: 'payments:delete',
    PAYMENTS_MANAGE: 'payments:manage',
    // Garage door quotes
    GARAGE_DOOR_QUOTES_VIEW: 'garage-door-quotes:view',
    GARAGE_DOOR_QUOTES_CREATE: 'garage-door-quotes:create',
    GARAGE_DOOR_QUOTES_UPDATE: 'garage-door-quotes:update',
    GARAGE_DOOR_QUOTES_DELETE: 'garage-door-quotes:delete',
    GARAGE_DOOR_QUOTES_MANAGE: 'garage-door-quotes:manage',
    // HVAC presentations
    HVAC_PRESENTATIONS_VIEW: 'hvac-presentations:view',
    HVAC_PRESENTATIONS_CREATE: 'hvac-presentations:create',
    HVAC_PRESENTATIONS_UPDATE: 'hvac-presentations:update',
    HVAC_PRESENTATIONS_DELETE: 'hvac-presentations:delete',
    HVAC_PRESENTATIONS_MANAGE: 'hvac-presentations:manage',
    // General access
    ADMIN_ACCESS: 'admin:access',
    // Audit logging
    AUDIT_VIEW: 'audit:view',
    AUDIT_MANAGE: 'audit:manage',
    // Call management
    CALLS_VIEW: 'call:view',
    CALLS_CREATE: 'call:create',
    CALLS_UPDATE: 'call:update',
    CALLS_DELETE: 'call:delete',
    CALLS_MANAGE: 'call:manage',
};
// Role-based permissions mapping
export const ROLE_PERMISSIONS = {
    [UserRole.SUPER_ADMIN]: Object.values(PERMISSIONS),
    [UserRole.OWNER]: Object.values(PERMISSIONS), // Business owner has all permissions
    [UserRole.ADMIN]: [
        PERMISSIONS.USERS_VIEW,
        PERMISSIONS.USERS_CREATE,
        PERMISSIONS.USERS_UPDATE,
        PERMISSIONS.USERS_MANAGE,
        PERMISSIONS.CUSTOMERS_VIEW,
        PERMISSIONS.CUSTOMERS_CREATE,
        PERMISSIONS.CUSTOMERS_UPDATE,
        PERMISSIONS.CUSTOMERS_DELETE,
        PERMISSIONS.CUSTOMERS_MANAGE,
        PERMISSIONS.JOBS_VIEW,
        PERMISSIONS.JOBS_CREATE,
        PERMISSIONS.JOBS_UPDATE,
        PERMISSIONS.JOBS_DELETE,
        PERMISSIONS.JOBS_ASSIGN,
        PERMISSIONS.JOBS_MANAGE,
        PERMISSIONS.ESTIMATES_VIEW,
        PERMISSIONS.ESTIMATES_CREATE,
        PERMISSIONS.ESTIMATES_UPDATE,
        PERMISSIONS.ESTIMATES_DELETE,
        PERMISSIONS.ESTIMATES_SEND,
        PERMISSIONS.ESTIMATES_MANAGE,
        PERMISSIONS.INVOICES_VIEW,
        PERMISSIONS.INVOICES_CREATE,
        PERMISSIONS.INVOICES_UPDATE,
        PERMISSIONS.INVOICES_DELETE,
        PERMISSIONS.INVOICES_SEND,
        PERMISSIONS.INVOICES_MANAGE,
        PERMISSIONS.BUSINESS_VIEW,
        PERMISSIONS.BUSINESS_UPDATE,
        PERMISSIONS.BUSINESS_MANAGE,
        PERMISSIONS.BUSINESS_SETTINGS,
        PERMISSIONS.BUSINESS_INTEGRATIONS,
        PERMISSIONS.REPORTS_VIEW,
        PERMISSIONS.REPORTS_EXPORT,
        PERMISSIONS.REPORTS_MANAGE,
        PERMISSIONS.TIME_CLOCK,
        PERMISSIONS.TIME_MANAGE,
        PERMISSIONS.TIME_VIEW,
        PERMISSIONS.TIME_APPROVE,
        PERMISSIONS.PRICEBOOK_VIEW,
        PERMISSIONS.PRICEBOOK_CREATE,
        PERMISSIONS.PRICEBOOK_UPDATE,
        PERMISSIONS.PRICEBOOK_DELETE,
        PERMISSIONS.PRICEBOOK_MANAGE,
        PERMISSIONS.INVENTORY_READ,
        PERMISSIONS.INVENTORY_WRITE,
        PERMISSIONS.INVENTORY_MANAGE,
        PERMISSIONS.SUPPLIER_NETWORK_READ,
        PERMISSIONS.SUPPLIER_NETWORK_WRITE,
        PERMISSIONS.SUPPLIER_NETWORK_MANAGE,
        PERMISSIONS.FLEET_READ,
        PERMISSIONS.FLEET_WRITE,
        PERMISSIONS.FLEET_ASSIGN,
        PERMISSIONS.FLEET_MANAGE,
        PERMISSIONS.AI_ACCESS,
        PERMISSIONS.ADMIN_ACCESS,
        PERMISSIONS.SCHEDULING_VIEW,
        PERMISSIONS.SCHEDULING_MANAGE,
        PERMISSIONS.SCHEDULING_CREATE,
        PERMISSIONS.SCHEDULING_UPDATE,
        PERMISSIONS.SCHEDULING_DELETE,
        PERMISSIONS.ROUTE_PLANNING_VIEW,
        PERMISSIONS.ROUTE_PLANNING_MANAGE,
        PERMISSIONS.ROUTE_PLANNING_CREATE,
        PERMISSIONS.ROUTE_PLANNING_UPDATE,
        PERMISSIONS.ROUTE_PLANNING_DELETE,
        PERMISSIONS.AUDIT_VIEW,
        PERMISSIONS.AUDIT_MANAGE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_VIEW,
        PERMISSIONS.GARAGE_DOOR_QUOTES_CREATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_UPDATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_DELETE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_MANAGE,
        PERMISSIONS.HVAC_PRESENTATIONS_VIEW,
        PERMISSIONS.HVAC_PRESENTATIONS_CREATE,
        PERMISSIONS.HVAC_PRESENTATIONS_UPDATE,
        PERMISSIONS.HVAC_PRESENTATIONS_DELETE,
        PERMISSIONS.HVAC_PRESENTATIONS_MANAGE,
        PERMISSIONS.PAYMENTS_VIEW,
        PERMISSIONS.PAYMENTS_CREATE,
        PERMISSIONS.PAYMENTS_UPDATE,
        PERMISSIONS.PAYMENTS_DELETE,
        PERMISSIONS.PAYMENTS_MANAGE,
        PERMISSIONS.EXPENSES_VIEW,
        PERMISSIONS.EXPENSES_CREATE,
        PERMISSIONS.EXPENSES_UPDATE,
        PERMISSIONS.EXPENSES_APPROVE,
        PERMISSIONS.EXPENSES_MANAGE,
    ],
    [UserRole.MANAGER]: [
        PERMISSIONS.USERS_VIEW,
        PERMISSIONS.CUSTOMERS_VIEW,
        PERMISSIONS.CUSTOMERS_CREATE,
        PERMISSIONS.CUSTOMERS_UPDATE,
        PERMISSIONS.JOBS_VIEW,
        PERMISSIONS.JOBS_CREATE,
        PERMISSIONS.JOBS_UPDATE,
        PERMISSIONS.JOBS_DELETE,
        PERMISSIONS.JOBS_ASSIGN,
        PERMISSIONS.ESTIMATES_VIEW,
        PERMISSIONS.ESTIMATES_CREATE,
        PERMISSIONS.ESTIMATES_UPDATE,
        PERMISSIONS.ESTIMATES_SEND,
        PERMISSIONS.INVOICES_VIEW,
        PERMISSIONS.INVOICES_CREATE,
        PERMISSIONS.INVOICES_UPDATE,
        PERMISSIONS.INVOICES_SEND,
        PERMISSIONS.BUSINESS_VIEW,
        PERMISSIONS.BUSINESS_SETTINGS,
        PERMISSIONS.BUSINESS_INTEGRATIONS,
        PERMISSIONS.REPORTS_VIEW,
        PERMISSIONS.TIME_CLOCK,
        PERMISSIONS.TIME_MANAGE,
        PERMISSIONS.TIME_VIEW,
        PERMISSIONS.TIME_APPROVE,
        PERMISSIONS.INVENTORY_READ,
        PERMISSIONS.INVENTORY_WRITE,
        PERMISSIONS.INVENTORY_MANAGE,
        PERMISSIONS.SUPPLIER_NETWORK_READ,
        PERMISSIONS.SUPPLIER_NETWORK_WRITE,
        PERMISSIONS.SUPPLIER_NETWORK_MANAGE,
        PERMISSIONS.FLEET_READ,
        PERMISSIONS.FLEET_WRITE,
        PERMISSIONS.FLEET_ASSIGN,
        PERMISSIONS.FLEET_MANAGE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_VIEW,
        PERMISSIONS.GARAGE_DOOR_QUOTES_CREATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_UPDATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_DELETE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_MANAGE,
        PERMISSIONS.HVAC_PRESENTATIONS_VIEW,
        PERMISSIONS.HVAC_PRESENTATIONS_CREATE,
        PERMISSIONS.HVAC_PRESENTATIONS_UPDATE,
        PERMISSIONS.HVAC_PRESENTATIONS_DELETE,
        PERMISSIONS.HVAC_PRESENTATIONS_MANAGE,
        PERMISSIONS.PAYMENTS_VIEW,
        PERMISSIONS.PAYMENTS_CREATE,
        PERMISSIONS.PAYMENTS_UPDATE,
        PERMISSIONS.PAYMENTS_DELETE,
        PERMISSIONS.PAYMENTS_MANAGE,
        PERMISSIONS.EXPENSES_VIEW,
        PERMISSIONS.EXPENSES_CREATE,
        PERMISSIONS.EXPENSES_UPDATE,
        PERMISSIONS.EXPENSES_APPROVE,
        PERMISSIONS.EXPENSES_MANAGE,
    ],
    [UserRole.TECHNICIAN]: [
        PERMISSIONS.USERS_VIEW, // Allow technicians to see team members
        PERMISSIONS.CUSTOMERS_VIEW,
        PERMISSIONS.CUSTOMERS_CREATE, // Allow technicians to create customers
        PERMISSIONS.CUSTOMERS_UPDATE, // Allow technicians to update customers
        PERMISSIONS.CUSTOMERS_DELETE, // Allow technicians to delete customers
        PERMISSIONS.JOBS_VIEW,
        PERMISSIONS.JOBS_CREATE, // Allow technicians to create jobs
        PERMISSIONS.JOBS_UPDATE,
        PERMISSIONS.JOBS_ASSIGN, // Allow technicians to assign jobs to themselves
        PERMISSIONS.ESTIMATES_VIEW,
        PERMISSIONS.ESTIMATES_CREATE,
        PERMISSIONS.INVOICES_VIEW,
        PERMISSIONS.INVOICES_CREATE, // Allow technicians to create invoices
        PERMISSIONS.INVOICES_UPDATE, // Allow technicians to update invoices
        PERMISSIONS.BUSINESS_VIEW, // Allow technicians to view business settings for Stripe
        PERMISSIONS.BUSINESS_SETTINGS, // Allow technicians to access business settings for integrations
        PERMISSIONS.BUSINESS_INTEGRATIONS, // Allow technicians to access integrations
        PERMISSIONS.TIME_CLOCK,
        PERMISSIONS.TIME_VIEW,
        PERMISSIONS.INVENTORY_READ, // Allow technicians to view inventory
        PERMISSIONS.INVENTORY_MANAGE, // Allow technicians to manage inventory
        PERMISSIONS.SUPPLIER_NETWORK_READ,
        PERMISSIONS.SUPPLIER_NETWORK_WRITE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_VIEW,
        PERMISSIONS.GARAGE_DOOR_QUOTES_CREATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_UPDATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_DELETE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_MANAGE,
        PERMISSIONS.HVAC_PRESENTATIONS_VIEW,
        PERMISSIONS.HVAC_PRESENTATIONS_CREATE,
        PERMISSIONS.HVAC_PRESENTATIONS_UPDATE,
        PERMISSIONS.HVAC_PRESENTATIONS_DELETE,
        PERMISSIONS.HVAC_PRESENTATIONS_MANAGE,
        PERMISSIONS.PAYMENTS_VIEW,
        PERMISSIONS.PAYMENTS_CREATE,
        PERMISSIONS.PAYMENTS_UPDATE,
        PERMISSIONS.PAYMENTS_DELETE,
        PERMISSIONS.PAYMENTS_MANAGE,
        PERMISSIONS.EXPENSES_VIEW,
        PERMISSIONS.EXPENSES_CREATE,
        PERMISSIONS.EXPENSES_UPDATE,
    ],
    [UserRole.OFFICE_STAFF]: [
        PERMISSIONS.USERS_VIEW, // Allow office staff to see team members
        PERMISSIONS.CUSTOMERS_VIEW,
        PERMISSIONS.CUSTOMERS_CREATE,
        PERMISSIONS.CUSTOMERS_UPDATE,
        PERMISSIONS.JOBS_VIEW,
        PERMISSIONS.JOBS_CREATE,
        PERMISSIONS.JOBS_UPDATE,
        PERMISSIONS.ESTIMATES_VIEW,
        PERMISSIONS.ESTIMATES_CREATE,
        PERMISSIONS.ESTIMATES_UPDATE,
        PERMISSIONS.ESTIMATES_SEND,
        PERMISSIONS.INVOICES_VIEW,
        PERMISSIONS.INVOICES_CREATE,
        PERMISSIONS.INVOICES_UPDATE,
        PERMISSIONS.INVOICES_SEND,
        PERMISSIONS.REPORTS_VIEW,
        PERMISSIONS.TIME_VIEW,
        PERMISSIONS.INVENTORY_READ, // Allow office staff to view inventory
        PERMISSIONS.INVENTORY_MANAGE, // Allow office staff to manage inventory
        PERMISSIONS.SUPPLIER_NETWORK_READ,
        PERMISSIONS.SUPPLIER_NETWORK_WRITE,
        PERMISSIONS.SUPPLIER_NETWORK_MANAGE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_VIEW,
        PERMISSIONS.GARAGE_DOOR_QUOTES_CREATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_UPDATE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_DELETE,
        PERMISSIONS.GARAGE_DOOR_QUOTES_MANAGE,
        PERMISSIONS.HVAC_PRESENTATIONS_VIEW,
        PERMISSIONS.HVAC_PRESENTATIONS_CREATE,
        PERMISSIONS.HVAC_PRESENTATIONS_UPDATE,
        PERMISSIONS.HVAC_PRESENTATIONS_DELETE,
        PERMISSIONS.HVAC_PRESENTATIONS_MANAGE,
        PERMISSIONS.PAYMENTS_VIEW,
        PERMISSIONS.PAYMENTS_CREATE,
        PERMISSIONS.PAYMENTS_UPDATE,
        PERMISSIONS.PAYMENTS_DELETE,
        PERMISSIONS.PAYMENTS_MANAGE,
        PERMISSIONS.EXPENSES_VIEW,
        PERMISSIONS.EXPENSES_CREATE,
        PERMISSIONS.EXPENSES_UPDATE,
    ],
};
//# sourceMappingURL=auth.js.map